import { useState, useRef, useEffect, useContext } from "react";
import InfoTable from "./InfoTable";
import { Tooltip, Dialog, DialogContent, DialogTitle, FormControl, MenuItem, Select } from "@mui/material";
import AddRow from "../../assets/img/icons/add.png";
import Expand from "../../assets/img/icons/expand2.png";
import Shrink from "../../assets/img/icons/shrink2.png";
import Loader from "../../helpers/Loader";
import {
  DistributionStatusConstants,
  distributionButtonsConfig,
  distributionStatuses,
} from "../../helpers/Constants";
import AuthContext from "../auth/AuthContext";
import Popup from "../../helpers/Popup";
import { showNotificationToast } from "../../helpers/Messages";
import SingleDropdown from "../../helpers/SingleDropdown";
import { transformDataToRows, commonOutlinedInputStyles, commonMenuItemStyles } from '../../helpers/InfoTableHelper';

const Info = ({
  selectedStructureId,
  selectedRequestId,
  trackingId,
  structureDetails,
  inspectionType,
  requestOrgId
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const [rows, setRows] = useState([]);
  const [tableType, setTableType] = useState("");
  const [showOptions, setShowOptions] = useState(false);
  const [tableHeight, setTableHeight] = useState(0);
  const [openPopup, setOpenPopup] = useState(false);
  const [reshootPopup, setReshootPopup] = useState(false);
  const [currentTemplateSchema, setCurrentTemplateSchema] = useState({});
  const [changesMade, setChangesMade] = useState(false);

  const dropdownRef = useRef(null);
  const tableRef = useRef(null);
  const infoTableRef = useRef(null);

  const authContext = useContext(AuthContext);
  const { email: userEmail, role: userRole } = authContext;

  const roleAuthorization = userRole === "Admin" || userRole === "Operations";
  const mailAuthorization =
    structureDetails?.analyst?.email === userEmail ||
    structureDetails?.reviewer?.email === userEmail;

  const fetchStructureInfoData = async () => {
    setLoading(true);
    let fetchedStructureData = null;

    try {
      const requestType = inspectionType === 'distribution' ? 2 : 1; 
      fetchedStructureData = await window.apiHelper.getStructureInfoData(trackingId, requestType);

      let templateSchema = {};
      let currentTemplateId = null;

      // check if existing data is present
      if (fetchedStructureData && fetchedStructureData.template_id) {
        try {
          const schema = await window.apiHelper.getTemplateSchema(fetchedStructureData.template_id);
          if (schema && Object.keys(schema).length > 0) {
            templateSchema = schema;
            currentTemplateId = fetchedStructureData.template_id;
          }
        } catch (schemaError) {
          console.error("Error fetching template schema:", schemaError);
          showNotificationToast("Error loading template schema for existing data.");
        }
      }

      setCurrentTemplateSchema(templateSchema);

      // if fetchedStructureData.data exists, use it
      // otherwise starts with an empty object.
      const dataToTransform = (fetchedStructureData && fetchedStructureData.data) ? fetchedStructureData.data : {};
      const transformedRows = transformDataToRows(dataToTransform, templateSchema);
      setRows(transformedRows);

      // determines if we should show template selection based on data presence AND if a template was successfully loaded/associated
      // if there's no data AND user hasn't selected an option, we should show options
      if (Object.keys(dataToTransform).length === 0 && !currentTemplateId) {
        setShowOptions(true);
        setSelectedOption(null);
        fetchAvailableTemplates(requestOrgId);
      } else if (currentTemplateId) { 
        // if there was a template ID, even if schema fetch failed
        setSelectedOption(currentTemplateId);
        setShowOptions(false); // don't show options if a template was referenced in data
      } else {
        // no existing data or no template ID, show template selection
        setShowOptions(true);
        setSelectedOption(null);
        fetchAvailableTemplates(requestOrgId);
      }
      
      // set tableType based on whether a template was found or if it's custom data
      if (currentTemplateId) {
        const templateName = dropdownOptions.find(opt => opt.id === currentTemplateId)?.name || "Existing Data";
        setTableType(templateName);
      } else if (Object.keys(dataToTransform).length > 0) {
        setTableType("Custom Data"); // for existing data without a template ID
      } else {
        setTableType(""); // no data, no template selected yet
      }

    } catch (error) {
      console.error("Error fetching structure data (main catch):", error);
      // if the initial getStructureInfoData fails completely, then we fall back to template selection
      setRows([]);
      setTableType("");
      setShowOptions(true); // allow template selection on error
      setSelectedOption(null);
      setCurrentTemplateSchema({});
      fetchAvailableTemplates(requestOrgId);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableTemplates = async (orgId) => {
    setLoadingTemplates(true);
    try {
      const availableTemplates = await window.apiHelper.getAvailableTemplates(orgId);
      if (availableTemplates && availableTemplates.length > 0) {
        setDropdownOptions(availableTemplates);
        // if entering template selection mode and no option is chosen, default to the first one
        // only set if not already set by existing data flow
        if (showOptions && !selectedOption) {
          setSelectedOption(availableTemplates[0].id);
          // load its schema immediately to show defaults
          const schema = await window.apiHelper.getTemplateSchema(availableTemplates[0].id);
          setCurrentTemplateSchema(schema);
          setRows(transformDataToRows({}, schema)); // populate with schema defaults (no existing data to merge here)
          setTableType(availableTemplates[0].name);
        }
      } else {
        setDropdownOptions([]);
      }
    } catch (error) {
      console.error("Error fetching available templates:", error);
      showNotificationToast("Error loading available templates.");
      setDropdownOptions([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // initial data load
  useEffect(() => {
    if (selectedStructureId) {
      fetchStructureInfoData();
    }
  }, [selectedStructureId]);

  // load schema when a template is selected from the dropdown
  useEffect(() => {
    const loadSchemaOnSelection = async () => {
      if (selectedOption && showOptions && dropdownOptions.length > 0) {
        setLoading(true);
        try {
          const templateSchema = await window.apiHelper.getTemplateSchema(selectedOption);
          setCurrentTemplateSchema(templateSchema);
          setChangesMade(true);
          // for new template selection, start with schema defaults
          setRows(transformDataToRows({}, templateSchema));
          const templateName = dropdownOptions.find(opt => opt.id === selectedOption)?.name || "Selected Template";
          setTableType(templateName);
        } catch (error) {
          console.error("Error fetching template schema:", error);
          showNotificationToast("Error loading template schema.");
          setChangesMade(false);
          setRows([]);
          setCurrentTemplateSchema({});
        } finally {
          setLoading(false);
        }
      }
    };
    loadSchemaOnSelection();
  }, [selectedOption, showOptions, dropdownOptions]);

  useEffect(() => {
    setSelectedStatus(structureDetails?.status);
  }, [structureDetails]);

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const handleSortOptionSelect = (optionId) => {
    setSelectedOption(optionId);
    setShowDropdown(false);
  };

  const handleStatusSelect = (selectedOption) => {
    setSelectedStatus(selectedOption);
    updateDistributionStatus(selectedOption);
  };

  const handleAddRow = () => {
    const newCustomRow = {
      key: `custom_${Date.now()}`,
      information: {
        value: "",
        type: "string",
        isCustom: true
      },
    };

    if (tableRef.current) {
      tableRef.current.addRow(newCustomRow);
    }
  };

  useEffect(() => {
    if (infoTableRef.current) {
      setTableHeight(infoTableRef.current.clientHeight);
    }
  }, []);

  const handlePopup = () => {
    setOpenPopup(!openPopup);
  };

  const renderHeader = (imgSrc) => (
    <div className="dropdown-header">
      <div className="dropdown-header-left">{renderDropdownOrTableType()}</div>
      <div className="dropdown-header-right">
        <Tooltip title={openPopup ? "Close Window" : "Expand View"} arrow placement="top">
          <button className="resize-button" onClick={handlePopup}>
            <img src={imgSrc} alt="expand" className="button-img-resize" />
          </button>
        </Tooltip>
        {renderAddRowButton()}
      </div>
    </div>
  );

  const renderDropdownOrTableType = () => {
    if (showOptions && authContext.permissions.includes("can_edit_info")) {
      if (loadingTemplates) {
        return null;
      } else if (dropdownOptions.length > 0) {
        return (
          <>
            <FormControl
              variant="outlined"
              sx={{ minWidth: 250, maxWidth: 325, verticalAlign: 'middle' }}
              size="small"
              >
              <Select
                value={selectedOption || ''} // MUI Select prefers empty string for no selection
                onChange={(event) => handleSortOptionSelect(event.target.value)}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected || selected === '') {
                    return <span style={{ color: '#9e9e9e' }}>Select Preset</span>;
                  }
                  return (
                    <span sx={{ color: '#54c1a5' }}>
                      {dropdownOptions.find(opt => opt.id === selected)?.name || "-----"}
                    </span>
                  );
                }}
                sx={commonOutlinedInputStyles}
              >
                {dropdownOptions.map((option) => (
                  <MenuItem
                    key={option.id}
                    value={option.id}
                    sx={commonMenuItemStyles}
                  >
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        );
      } else {
        // if showOptions is true, user has permission, but there are no templates for that org_id
        return (
          <div style={{ marginRight: "5px" }}>
            No presets available.
          </div>
        );
      }
    } else {
      // default case: showOptions is false or user doesn't have permission
      return (
        <div>{tableType}</div>
      );
    }
  };

  const renderAddRowButton = () => {
    return (
      authContext.permissions.includes("can_edit_info") && (
        <Tooltip title="Add New Row" arrow placement="top">
          {(roleAuthorization || mailAuthorization) && (
            <button onClick={handleAddRow} className="save-btn">
              <img src={AddRow} alt="add new row" style={{ width: "35px" }} />
            </button>
          )}
        </Tooltip>
      )
    );
  };

  const renderInfoTable = (tableHeight) => (
    <InfoTable
      ref={tableRef}
      rows={rows}
      setRows={setRows}
      selectedStructureId={selectedStructureId}
      selectedRequestId={selectedRequestId}
      selectedOption={selectedOption}
      setLoading={setLoading}
      setShowOptions={setShowOptions}
      tableHeight={tableHeight}
      statusButtons={statusButtons}
      currentTemplateSchema={currentTemplateSchema}
      inspectionType={inspectionType}
      trackingId={trackingId}
      changesMade={changesMade}
      setChangesMade={setChangesMade}
    />
  );

  const updateDistributionStatus = async (value) => {
    setLoading(true);
    try {
      const status = distributionStatuses[value];
      if (!status) {
        console.error("Invalid status type:", status.label);
        return;
      }

      const formatted_data = {
        id: trackingId,
        status: status.value,
      };

      await window.apiHelper.singleUpdateStructure(trackingId, formatted_data);
      showNotificationToast(`Status updated to ${status.label}`);
    } catch (err) {
      console.error("Error updating status:", err);
      showNotificationToast("Error updating status.");
    } finally {
      setLoading(false);
    }
  };

  const renderDistributionButtons = () => {
    if (!mailAuthorization || !structureDetails?.status) return null;

    return distributionButtonsConfig
      .filter(({ condition }) => condition(structureDetails.status, userRole))
      .map(({ label, nextStatus }) => {
        const option = distributionStatuses[nextStatus];

        if (!option) return null;

        return (
          <div
            key={label}
            className="status-button"
            style={{
              backgroundColor: option.backgroundColor,
              color: option.color,
            }}
            onClick={() => handleChangeDistributionStatus(nextStatus)}
          >
            {label}
          </div>
        );
      });
  };

  const handleChangeDistributionStatus = (value) => {
    if (value === DistributionStatusConstants.reshoot) {
      setReshootPopup(true);
    } else {
      updateDistributionStatus(value);
    }
  };

  const handleReshootPopupAccept = () => {
    setReshootPopup(false);
    updateDistributionStatus(DistributionStatusConstants.reshoot);
  };

  const handleReshootPopupReject = () => {
    setReshootPopup(false);
  };

  const getFilteredStatuses = (role) => {
    const excluded = role === "Reviewer" ? [1, 2, 7, 8] : [];
    return Object.values(
      Object.fromEntries(
        Object.entries(distributionStatuses).filter(
          ([key]) => !excluded.includes(Number(key))
        )
      )
    );
  };

  const statusButtons = (
    <div className="status-buttons">
      {(roleAuthorization ||
        (userRole === "Reviewer" && mailAuthorization)) && (
        <div className="button-dropdown-container">
          <SingleDropdown
            options={getFilteredStatuses(userRole)}
            optionSelected={handleStatusSelect}
            selectedOption={selectedStatus}
            toggleDropdown={toggleDropdown}
            className="sort-dropdown"
            placement="top"
          />
        </div>
      )}
      {renderDistributionButtons()}
    </div>
  );

  return (
    <div style={{ position: "relative", height: "100%" }}>
      {reshootPopup && (
        <Popup
          text="Are you sure you want to reshoot ?"
          onAccept={handleReshootPopupAccept}
          onReject={handleReshootPopupReject}
          type="option"
        />
      )}
      {loading && <Loader />}
      <div
        className="button-dropdown-container"
        ref={dropdownRef}
        style={{ width: "100%" }}
      >
        {renderHeader(Expand)}
      </div>
      <div ref={infoTableRef} style={{ height: "calc(100% - 100px)" }}>
        {renderInfoTable(tableHeight)}
      </div>

      <Dialog open={openPopup} onClose={handlePopup} maxWidth="xl" fullWidth>
        <div style={{ position: "relative" }}>
          {loading && <Loader />}

          <DialogTitle>{renderHeader(Shrink)}</DialogTitle>
          <DialogContent dividers>
            {renderInfoTable(tableHeight * 1.25)}
          </DialogContent>
        </div>
      </Dialog>
    </div>
  );
};

export default Info;